/** @velyn-custom BEGIN — vely-customer-reviews.js
 *  Figma "Das sagen unsere Kunden" — NPS-style bleed + slidesOffsetBefore + pill pagination.
 *  Desktop: slidesPerView "auto" + fixed 400px slide width (gap via spaceBetween).
 *  Mobile/tablet: numeric slidesPerView. Reuses window.Swiper + VelySwiperPagination.
 */
(function () {
  const getSwiper = () => window.Swiper;

  class VelyCustomerReviews extends HTMLElement {
    /**
     * Left inset = header .container content start (pad + center gutter).
     * slidesOffsetBefore moves with the track — free-bleed on slide, no jump.
     * Mobile/tablet (<1025px): fixed 1rem from the left edge.
     */
    getLeftInsetPx() {
      if (window.matchMedia("(max-width: 1024.98px)").matches) {
        const rootSize =
          parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
        return Math.round(rootSize);
      }

      const section = this.closest(".section-vely-customer-reviews");
      const shell = this.querySelector(".vely-crr__slider-shell");
      if (!section || !shell) return 0;

      const titleBox = section.querySelector(".vely-crr__header-wrap");
      const shellLeft = shell.getBoundingClientRect().left;

      if (titleBox) {
        const pad =
          parseFloat(getComputedStyle(titleBox).paddingInlineStart) || 0;
        return Math.max(
          0,
          Math.round(titleBox.getBoundingClientRect().left + pad - shellLeft)
        );
      }

      const probe = document.createElement("div");
      probe.setAttribute("aria-hidden", "true");
      probe.style.cssText =
        "position:absolute;visibility:hidden;pointer-events:none;height:0;width:var(--vely-crr-left-inset);";
      section.appendChild(probe);
      const px = Math.round(probe.getBoundingClientRect().width) || 0;
      probe.remove();
      return Math.max(0, px);
    }

    applyLeftOffset(swiper = this._swiper) {
      if (!swiper) return;
      const inset = this.getLeftInsetPx();
      if (swiper.params.slidesOffsetBefore !== inset) {
        swiper.params.slidesOffsetBefore = inset;
        swiper.update();
      } else {
        swiper.updateSize?.();
        swiper.updateSlides?.();
        swiper.updateProgress?.();
      }
      this.syncControls(swiper);
    }

    connectedCallback() {
      if (this._velyCrrInitStarted) return;
      this._velyCrrInitStarted = true;
      this.waitForSwiper(() => this.init());
    }

    disconnectedCallback() {
      if (this._swiper) {
        this._swiper.destroy(true, true);
        this._swiper = null;
      }
      this._velyCrrInitStarted = false;
      this.classList.remove("vely-crr--ready");
    }

    waitForSwiper(callback, attempt = 0) {
      const hasSwiper = typeof getSwiper() === "function";
      const hasPagination = Boolean(window.VelySwiperPagination?.refresh);
      if (hasSwiper && hasPagination) {
        callback();
        return;
      }
      if (attempt > 160) {
        console.warn(
          "[vely-customer-reviews] Swiper or VelySwiperPagination not available"
        );
        return;
      }
      setTimeout(() => this.waitForSwiper(callback, attempt + 1), 50);
    }

    refreshPagination() {
      if (!this._swiper || !this._paginationEl) return;
      window.VelySwiperPagination?.refresh?.(this._swiper, this._paginationEl);
      this.syncControls(this._swiper);
    }

    /**
     * Hide pagination when all cards fit (no horizontal overflow).
     * slidesPerView:"auto" makes shared bullet-count heuristics unreliable.
     */
    syncControls(swiper) {
      const controls = this.querySelector("[data-vely-pagination-anchor]");
      if (!controls || !swiper) return;

      swiper.update?.();

      const minT =
        typeof swiper.minTranslate === "function" ? swiper.minTranslate() : 0;
      const maxT =
        typeof swiper.maxTranslate === "function" ? swiper.maxTranslate() : 0;
      const travel = Math.abs(maxT - minT);
      const snaps = swiper.snapGrid?.length ?? 0;
      const canMove = Boolean(swiper.allowSlideNext || swiper.allowSlidePrev);
      const canSlide = !swiper.isLocked && canMove && snaps > 1 && travel > 1;

      controls.hidden = !canSlide;
      controls.setAttribute("aria-hidden", canSlide ? "false" : "true");
    }

    parseSlidesPerView(val, fallback) {
      if (val === "auto") return "auto";
      const n = parseFloat(val);
      return Number.isFinite(n) ? n : fallback;
    }

    init() {
      const SwiperCtor = getSwiper();
      if (typeof SwiperCtor !== "function") return;

      const swiperEl = this.querySelector(".vely-crr__swiper");
      if (!swiperEl) return;

      if (this._swiper) {
        this._swiper.destroy(true, true);
        this._swiper = null;
      }

      const num = (val, fallback) => {
        const n = parseFloat(val);
        return Number.isFinite(n) ? n : fallback;
      };

      const gapDesktop = num(swiperEl.dataset.spacing, 24);
      const gapMobile = num(swiperEl.dataset.spacingMobile, 16);
      const speed = num(swiperEl.dataset.speed, 500);
      const perDesktop = this.parseSlidesPerView(swiperEl.dataset.desktop, "auto");
      const perTablet = this.parseSlidesPerView(swiperEl.dataset.tablet, 2);
      const perMobile = this.parseSlidesPerView(swiperEl.dataset.mobile, 1.15);
      const useLoop = this.dataset.loop === "true";
      const autoplayOn = swiperEl.dataset.autoplay === "true";
      const leftInset = this.getLeftInsetPx();

      let autoplay = false;
      if (autoplayOn) {
        autoplay = {
          delay: num(swiperEl.dataset.autoPlaySpeed, 5000),
          pauseOnMouseEnter: true,
        };
      }

      this._paginationEl = this.querySelector(".vely-swiper-pagination-pill");

      try {
        this._swiper = new SwiperCtor(swiperEl, {
          speed,
          loop: useLoop,
          autoplay,
          grabCursor: true,
          watchSlidesProgress: true,
          watchOverflow: true,
          observer: true,
          observeParents: true,
          spaceBetween: gapMobile,
          slidesPerView: perMobile,
          slidesOffsetBefore: leftInset,
          breakpoints: {
            768: {
              spaceBetween: gapDesktop,
              slidesPerView: perTablet,
            },
            1025: {
              spaceBetween: gapDesktop,
              slidesPerView: perDesktop,
            },
          },
          on: {
            init: (swiper) => {
              this.applyLeftOffset(swiper);
              swiper.update();
              this.syncControls(swiper);
            },
            slideChange: (swiper) => {
              if (!this._paginationEl) return;
              window.VelySwiperPagination?.setActiveBullet?.(
                swiper,
                this._paginationEl
              );
            },
            resize: () => {
              this.applyLeftOffset(this._swiper);
              this.refreshPagination();
            },
            breakpoint: (swiper) => {
              this.applyLeftOffset(swiper);
              swiper.update();
              this.syncControls(swiper);
            },
          },
        });
      } catch (error) {
        this._swiper = null;
        console.warn("[vely-customer-reviews] Swiper init failed", error);
        return;
      }

      this.refreshPagination();
      requestAnimationFrame(() => {
        this.applyLeftOffset(this._swiper);
        this._swiper?.update?.();
        this.refreshPagination();
      });
      this.classList.add("vely-crr--ready");
    }
  }

  if (!customElements.get("vely-customer-reviews")) {
    customElements.define("vely-customer-reviews", VelyCustomerReviews);
  }

  document.addEventListener("shopify:section:load", (event) => {
    const host = event.target?.querySelector?.("vely-customer-reviews");
    if (host) {
      host._velyCrrInitStarted = false;
      host.connectedCallback();
    }
    initReviewsIoHosts(event.target);
  });

  const REVIEWS_IO_WIDGET_SELECTORS = [
    ".ruk_widget",
    ".ReviewsWidget",
    "[data-reviews-io-carousel-mount] > *",
    ".CarouselWidget",
  ].join(", ");

  function markReviewsIoLoaded(host) {
    if (!host || host.classList.contains("reviews-io-loaded")) return false;
    if (host.querySelector(REVIEWS_IO_WIDGET_SELECTORS)) {
      host.classList.add("reviews-io-loaded");
      return true;
    }
    return false;
  }

  function initReviewsIoHosts(root = document) {
    root.querySelectorAll?.("[data-reviews-io-carousel-widget]")?.forEach((host) => {
      if (markReviewsIoLoaded(host)) return;
      if (host._velyReviewsIoObserved) return;
      host._velyReviewsIoObserved = true;

      const observer = new MutationObserver(() => {
        if (markReviewsIoLoaded(host)) observer.disconnect();
      });
      observer.observe(host, { childList: true, subtree: true });
      setTimeout(() => observer.disconnect(), 30000);
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initReviewsIoHosts());
  } else {
    initReviewsIoHosts();
  }
})();
/** @velyn-custom END — vely-customer-reviews.js */
